import React from 'react';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { Banner } from 'components/banner';
import { DISMISSAL_DURATIONS } from 'components/banner/constants/dismissalDurations';
import { trackEvent } from 'utils/google-tag-manager';
import { TenantId } from '@tenants/types';
import { useTranslation } from 'next-i18next';

export interface PageBannerConfig {
  title?: string | Record<string, string>;
  message?: string | Record<string, string>;
  buttonText?: string | Record<string, string>;
  isVisible?: boolean;
  iconInlineWithTitle?: boolean;
  showIcon?: boolean;
  showCloseButton?: boolean;
  severity?: 'error' | 'warning' | 'info' | 'success';
  icon?: React.ReactNode;
  gtmEventName?: string;
  gtmEventCategory?: string;
  gtmEventLabel?: string;
  gtmButtonClickEventName?: string;
  gtmCloseEventName?: string;
  surveyUrl?: string;
}

/**
 * Helper function to get localized text from a string or locale object.
 * 
 * @param text - Either a string (for backward compatibility) or an object with locale keys
 * @param currentLocale - The current locale (e.g., 'en', 'fr', 'fr-CA')
 * @param fallback - Fallback text if no match is found
 * @returns The localized text string
 */
const getLocalizedText = (
  text: string | Record<string, string> | undefined,
  currentLocale: string,
  fallback = ''
): string => {
  if (!text) return fallback;
  if (typeof text === 'string') return text; // Backward compatibility with plain strings
  
  // Try current locale first (e.g., 'fr-CA')
  if (text[currentLocale]) return text[currentLocale];
  
  // Try base locale (e.g., 'fr-CA' -> 'fr')
  const baseLocale = currentLocale.split('-')[0];
  if (text[baseLocale]) return text[baseLocale];
  
  // Fall back to English
  if (text['en']) return text['en'];
  
  // Last resort: return first available value or fallback
  return fallback;
};

interface PageBannerProps {
  tenantId: TenantId;
  featureFlag: string;
  defaultConfig: PageBannerConfig;
  storageKey: string;
  storageStrategy?: 'cookie' | 'localStorage' | 'sessionStorage' | 'memory';
  dismissalDuration?: number;
  onDismiss?: () => void;
  testId: string;
}

const PageBanner: React.FC<PageBannerProps> = ({
  tenantId,
  featureFlag,
  defaultConfig,
  storageKey,
  storageStrategy = 'cookie',
  dismissalDuration = DISMISSAL_DURATIONS.ONE_DAY,
  onDismiss,
  testId,
}) => {
  const growthbook = useGrowthBook();
  const { i18n } = useTranslation();
  const currentLocale = i18n.language || 'en';

  const growthbookConfig = growthbook.getFeatureValue<PageBannerConfig>(
    featureFlag,
    defaultConfig
  );

  // Merge GrowthBook config with defaults to ensure all properties are preserved
  const config = {
    ...defaultConfig,
    ...growthbookConfig,
  };
  const { iconInlineWithTitle, showIcon } = config;

  // Resolve localized text for message and buttonText
  const message = getLocalizedText(config.message, currentLocale);
  const buttonText = getLocalizedText(config.buttonText, currentLocale);

  // Handle title - can be string or locale object
  const isLocaleObject = (value: unknown): value is Record<string, string> => {
    return typeof value === 'object' && value !== null && !React.isValidElement(value) && !Array.isArray(value);
  };
  
  let title: string;
  if (isLocaleObject(config.title)) {
    title = getLocalizedText(config.title, currentLocale);
  } else {
    title = config.title || '';
  }

  if (!config?.isVisible || !message) {
    return null;
  }

  const handleButtonClick = () => {
    // Track GTM event for button click (survey click)
    if (config.gtmButtonClickEventName) {
      trackEvent(config.gtmButtonClickEventName);
    }

    // Open survey URL in new tab if provided
    if (config.surveyUrl) {
      window.open(config.surveyUrl, '_blank', 'noopener,noreferrer');
    }

    onDismiss?.();
  };

  const handleCloseClick = () => {
    // Track GTM event for close button click
    if (config.gtmCloseEventName) {
      trackEvent(config.gtmCloseEventName);
    }

    onDismiss?.();
  };

  return (
    <Banner
      variant="floating"
      tenantId={tenantId}
      message={message}
      title={title}
      severity={config.severity || 'info'}
      showIcon={iconInlineWithTitle ? false : showIcon}
      icon={config.icon}
      iconInlineWithTitle={iconInlineWithTitle}
      buttonText={buttonText}
      storageKey={storageKey}
      storageStrategy={storageStrategy}
      dismissalDuration={dismissalDuration}
      onDismiss={handleCloseClick}
      onButtonClick={buttonText ? handleButtonClick : undefined}
      testId={testId}
    />
  );
};

export default PageBanner;